import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Building2, User, MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import MatrixBackground from './MatrixBackground';
import './RequestQuote.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestQuote = () => {
    // Set page title
  useEffect(() => {
    document.title = "Request a Quote - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    lawFirm: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.lawFirm || !formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/request-quote`, formData);
      
      toast.success('Quote request submitted successfully! Our sales team will contact you within 24 hours.');
      
      // Reset form
      setFormData({
        lawFirm: '',
        name: '',
        email: '',
        phone: '',
        message: ''
      });

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Error submitting quote request:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit request. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#0a0a0a', 
      padding: '2rem', 
      position: 'relative' 
    }}>
      <MatrixBackground />
      
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto', 
        position: 'relative', 
        zIndex: 1 
      }}>
        <Button
          variant="ghost"
          style={{ 
            color: '#D4AF37', 
            marginBottom: '1.5rem',
            background: 'transparent'
          }}
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>

        <Card style={{ 
          background: '#ffffff', 
          border: '1px solid rgba(212, 175, 55, 0.3)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardHeader style={{ background: 'transparent' }}>
            <CardTitle style={{ 
              color: '#000000', 
              fontSize: '2rem', 
              fontWeight: '700',
              background: 'transparent',
              marginBottom: '0.5rem'
            }}>
              Request Enterprise Quote
            </CardTitle>
            <CardDescription style={{ 
              color: '#000000', 
              fontSize: '1rem',
              background: 'transparent'
            }}>
              Tell us about your law firm and requirements. Our sales team will get back to you with a custom quote.
            </CardDescription>
          </CardHeader>

          <CardContent style={{ background: 'transparent' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Input
                  id="lawFirm"
                  type="text"
                  placeholder="Law Firm Name *"
                  value={formData.lawFirm}
                  onChange={(e) => setFormData({ ...formData, lawFirm: e.target.value })}
                  style={{
                    background: '#f4f3ee',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{
                      background: '#f4f3ee',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#000000',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                    required
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={{
                      background: '#f4f3ee',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#000000',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Phone Number (Optional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={{
                    background: '#f4f3ee',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Label htmlFor="message" style={{ 
                  color: '#000000', 
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <MessageSquare size={18} />
                  Tell us about your requirements *
                </Label>
                <textarea
                  id="message"
                  placeholder="Number of users, specific features needed, integration requirements, etc."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  style={{
                    background: '#f4f3ee',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#000000',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                  rows="6"
                  required
                />
              </div>

              <div style={{
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h4 style={{
                  color: '#000000',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  margin: '0 0 1rem 0'
                }}>Enterprise Plan Includes:</h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.75rem'
                }}>
                  <li style={{ color: '#000000', fontSize: '0.9375rem' }}>✓ Unlimited credits</li>
                  <li style={{ color: '#000000', fontSize: '0.9375rem' }}>✓ Unlimited users</li>
                  <li style={{ color: '#000000', fontSize: '0.9375rem' }}>✓ Dedicated account manager</li>
                  <li style={{ color: '#000000', fontSize: '0.9375rem' }}>✓ Custom integrations</li>
                  <li style={{ color: '#000000', fontSize: '0.9375rem' }}>✓ Priority 24/7 support</li>
                  <li style={{ color: '#000000', fontSize: '0.9375rem' }}>✓ On-premise deployment options</li>
                  <li style={{ color: '#000000', fontSize: '0.9375rem' }}>✓ Custom training and onboarding</li>
                </ul>
              </div>

              <Button
                type="submit"
                style={{
                  background: 'linear-gradient(to right, #D4AF37, #E5B200)',
                  color: '#000000',
                  fontWeight: '600',
                  padding: '1rem 2rem',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  border: 'none',
                  width: '100%'
                }}
                disabled={loading}
              >
                {loading ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send size={18} />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RequestQuote;
